// CORE-RECEIPTS (core delivery plan item 4): an approved outward act's receipt on its approval's
// page — who approved it and when, the policy that made it an approval, the rulings and memory
// records behind it, how it went out and the provider's message id. Ids and names only.
// Calls on load: `outbox.list?approval={id}` (the act the approval put in the outbox; none, or one
// without a receipt — the company flag `outbox_executors` off — renders nothing).
import type { OperationOutput } from "@companyos/sdk";
import { useQuery } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { Facts, Section } from "../screen-parts";
import { outboxKey } from "./outbox-screen";

export type OutboxReceipt = NonNullable<OperationOutput<"outbox.list">["data"][number]["receipt"]>;

const ids = (xs: readonly string[]) => (xs.length > 0 ? xs.join(", ") : "None");

/** The receipt's facts, in reading order: pure (tested). */
export function receiptFacts(r: OutboxReceipt): { label: string; value: string }[] {
  return [
    {
      label: "Approved by",
      value: `${r.approver.name ?? "A person"}${r.approved_at ? ` · ${formatDateTime(r.approved_at)}` : ""}`,
    },
    {
      label: "Policy",
      value: r.policy
        ? `${r.policy.mode} at ${r.policy.level}${r.policy.scope_id ? ` (${r.policy.scope_id})` : ""}`
        : "Asked by a person",
    },
    { label: "Precedents", value: ids(r.precedent_ids) },
    { label: "Records read", value: ids(r.record_ids) },
    {
      label: "Sent through",
      value:
        r.executor.mode === "live"
          ? r.executor.provider
          : `Logged only: ${r.executor.why ?? "nothing left CompanyOS"}`,
    },
    { label: "Provider message id", value: r.provider_message_id ?? "None yet" },
    { label: "Queued", value: formatDateTime(r.queued_at) },
    { label: "Sent", value: r.sent_at ? formatDateTime(r.sent_at) : "Not yet" },
  ];
}

export function OutboxReceiptSection({
  api,
  companyId,
  approvalId,
}: {
  api: Api;
  companyId: string;
  approvalId: string;
}) {
  const q = useQuery({
    queryKey: [...outboxKey(companyId), "approval", approvalId],
    queryFn: () =>
      api.client.outbox.list({
        params: { co: companyId },
        query: { approval: approvalId, limit: 1 },
      }),
    retry: false,
  });
  const receipt = q.data?.data[0]?.receipt ?? null;
  if (!receipt) return null;
  return (
    <Section id="approval-receipt" title="Receipt">
      <Facts items={receiptFacts(receipt)} />
    </Section>
  );
}
